import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import DraftControls from '../components/DraftControls';
import PageSectionsManager from '../components/PageSectionsManager';
import BulkConvertSpeakers from '../components/BulkConvertSpeakers';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  Save, RefreshCw, ChevronDown, ChevronUp, Plus, Trash2, Edit2, X, Check,
  Clock, Calendar, MapPin, Users, Eye, EyeOff, GripVertical, Copy, Archive,
  AlertTriangle, Search, Filter, ExternalLink, Undo, Redo, Bold, Italic,
  Underline, List, ListOrdered, Link, Type, FileText, Settings, History,
  Play, Pause, ChevronLeft, ChevronRight, Star, Mic, Coffee, Award,
  AlertCircle, CheckCircle, XCircle, Info, Send, FileCheck, RotateCcw
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Session Type Configuration
const SESSION_TYPES = {
  keynote: { label: 'Keynote', color: '#D4AF37', icon: Award },
  panel: { label: 'Panel', color: '#00457f', icon: Users },
  ceremony: { label: 'Ceremony', color: '#5B2C6F', icon: Star },
  networking: { label: 'Networking', color: '#1A5276', icon: Users },
  tour: { label: 'Tour', color: '#2D5016', icon: MapPin },
  reception: { label: 'Reception', color: '#922B21', icon: Coffee },
  break: { label: 'Break', color: '#6B7280', icon: Coffee },
  lunch: { label: 'Lunch', color: '#059669', icon: Coffee }
};

// ==================== VALIDATION UTILITIES ====================

const parseTimeToMinutes = (timeStr) => {
  if (!timeStr) return null;
  const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (!match) return null;
  let hours = parseInt(match[1]);
  const minutes = parseInt(match[2]);
  const period = match[3].toUpperCase();
  if (period === 'PM' && hours !== 12) hours += 12;
  if (period === 'AM' && hours === 12) hours = 0;
  return hours * 60 + minutes;
};

const validateSession = (session, allSessions) => {
  const errors = [];
  const warnings = [];

  // Required field validation
  if (!session.title?.trim()) {
    errors.push({ field: 'title', message: 'Session title is required' });
  }

  // Time validation — times are optional. Only validate order when BOTH are present.
  const startMinutes = parseTimeToMinutes(session.start_time);
  const endMinutes = parseTimeToMinutes(session.end_time);

  if (startMinutes !== null && endMinutes !== null) {
    if (endMinutes <= startMinutes) {
      errors.push({ field: 'time', message: 'End time must be after start time' });
    }
    // NOTE: We intentionally do NOT flag overlaps across sessions here —
    // the program routinely runs parallel sessions in different rooms,
    // so overlapping times are expected (not a conflict).
  }

  // Content warnings
  if (!session.description?.trim()) {
    warnings.push({ field: 'description', message: 'Session description is empty' });
  }
  // Location and Room are both optional; no warning when either is missing.
  if (session.session_type !== 'break' && session.session_type !== 'lunch' && 
      (!session.speaker_ids || session.speaker_ids.length === 0)) {
    warnings.push({ field: 'speakers', message: 'No speakers assigned' });
  }

  return { errors, warnings, isValid: errors.length === 0 };
};

const validateAllSessions = (days) => {
  const allValidation = { totalErrors: 0, totalWarnings: 0, sessionValidations: {} };
  
  const allSessions = days?.flatMap(day => day.sessions || []) || [];
  
  allSessions.forEach(session => {
    const validation = validateSession(session, allSessions);
    allValidation.sessionValidations[session.id] = validation;
    allValidation.totalErrors += validation.errors.length;
    allValidation.totalWarnings += validation.warnings.length;
  });

  return allValidation;
};

// ==================== RICH TEXT EDITOR ====================

const RichTextEditor = ({ value, onChange, placeholder }) => {
  const editorRef = useRef(null);
  
  const execCommand = (command, val = null) => {
    document.execCommand(command, false, val);
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="flex items-center gap-1 p-2 bg-gray-50 border-b">
        <button type="button" onClick={() => execCommand('bold')} className="p-1.5 hover:bg-gray-200 rounded" title="Bold">
          <Bold className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => execCommand('italic')} className="p-1.5 hover:bg-gray-200 rounded" title="Italic">
          <Italic className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => execCommand('underline')} className="p-1.5 hover:bg-gray-200 rounded" title="Underline">
          <Underline className="w-4 h-4" />
        </button>
        <div className="w-px h-5 bg-gray-300 mx-1" />
        <button type="button" onClick={() => execCommand('insertUnorderedList')} className="p-1.5 hover:bg-gray-200 rounded" title="Bullet List">
          <List className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => execCommand('insertOrderedList')} className="p-1.5 hover:bg-gray-200 rounded" title="Numbered List">
          <ListOrdered className="w-4 h-4" />
        </button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        className="p-3 min-h-[100px] focus:outline-none prose prose-sm max-w-none"
        dangerouslySetInnerHTML={{ __html: value || '' }}
        onInput={() => editorRef.current && onChange(editorRef.current.innerHTML)}
        data-placeholder={placeholder}
      />
    </div>
  );
};

// ==================== TIME INPUT ====================

const TimeInput = ({ value, onChange, label, error }) => {
  const [inputValue, setInputValue] = useState(value || '');

  const parseTime = (input) => {
    const cleaned = input.toLowerCase().replace(/\s/g, '');
    const match = cleaned.match(/^(\d{1,2}):?(\d{2})?\s*(a|p|am|pm)?$/i);
    if (match) {
      let hours = parseInt(match[1]);
      const minutes = match[2] ? match[2] : '00';
      const period = match[3] ? (match[3].startsWith('p') ? 'PM' : 'AM') : (hours < 8 || hours === 12 ? 'PM' : 'AM');
      if (hours > 12) hours -= 12;
      return `${hours}:${minutes} ${period}`;
    }
    return input;
  };

  const handleBlur = () => {
    const parsed = parseTime(inputValue);
    setInputValue(parsed);
    onChange(parsed);
  };

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  return (
    <div>
      {label && <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>}
      <input
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onBlur={handleBlur}
        placeholder="e.g., 9:30 AM"
        className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#00457f] focus:border-transparent ${
          error ? 'border-red-500 bg-red-50' : ''
        }`}
      />
    </div>
  );
};

// ==================== VALIDATION BADGE ====================

const ValidationBadge = ({ validation }) => {
  if (!validation) return null;
  
  const { errors, warnings } = validation;
  
  if (errors.length === 0 && warnings.length === 0) {
    return (
      <span className="flex items-center gap-1 text-xs text-green-600">
        <CheckCircle className="w-3 h-3" />
      </span>
    );
  }

  return (
    <div className="flex items-center gap-2">
      {errors.length > 0 && (
        <span className="flex items-center gap-1 text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded">
          <XCircle className="w-3 h-3" />
          {errors.length}
        </span>
      )}
      {warnings.length > 0 && (
        <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded">
          <AlertTriangle className="w-3 h-3" />
          {warnings.length}
        </span>
      )}
    </div>
  );
};

// ==================== SORTABLE SESSION CARD ====================

const SortableSessionCard = ({ 
  session, 
  onEdit, 
  onDelete, 
  onDuplicate, 
  speakers,
  rooms = [],
  validation,
  isDraft
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: session.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  const typeConfig = SESSION_TYPES[session.session_type] || SESSION_TYPES.panel;
  const TypeIcon = typeConfig.icon;
  const hasErrors = validation?.errors?.length > 0;

  // Get assigned speakers
  const assignedSpeakers = speakers.filter(s => session.speaker_ids?.includes(s.id));
  // Get assigned room (if any)
  const assignedRoom = session.room_id ? rooms.find(r => r.id === session.room_id) : null;

  return (
    <div 
      ref={setNodeRef}
      style={style}
      className={`bg-white rounded-lg border-l-4 shadow-sm hover:shadow-md transition-all ${
        hasErrors ? 'border-red-500' : ''
      } ${isDragging ? 'z-50' : ''}`}
      style={{ ...style, borderLeftColor: hasErrors ? undefined : typeConfig.color }}
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* Drag Handle */}
          <div 
            {...attributes} 
            {...listeners} 
            className="cursor-grab pt-1 text-gray-400 hover:text-gray-600 active:cursor-grabbing"
          >
            <GripVertical className="w-5 h-5" />
          </div>

          {/* Time */}
          <div className="w-24 flex-shrink-0">
            {session.start_time || session.end_time ? (
              <>
                <div className="flex items-center gap-1 text-sm text-gray-500">
                  <Clock className="w-3 h-3" />
                  <span>{session.start_time || '--:--'}</span>
                </div>
                <div className="text-xs text-gray-400 mt-0.5">
                  to {session.end_time || '--:--'}
                </div>
              </>
            ) : (
              <div className="flex items-center gap-1 text-xs text-gray-400 italic">
                <Clock className="w-3 h-3" />
                <span>TBD</span>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span 
                className="text-xs px-2 py-0.5 rounded-full font-medium text-white"
                style={{ backgroundColor: typeConfig.color }}
              >
                {typeConfig.label}
              </span>
              {session.highlighted && (
                <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
              )}
              {session.visible === false && (
                <EyeOff className="w-4 h-4 text-gray-400" />
              )}
              <ValidationBadge validation={validation} />
              {isDraft && (
                <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">Draft</span>
              )}
            </div>
            <h4 className="font-semibold text-gray-900 truncate">{session.title || 'Untitled Session'}</h4>
            
            {/* Location & Speakers */}
            <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
              {assignedRoom && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium text-white"
                  style={{ backgroundColor: assignedRoom.color || '#00457f' }}
                  title={assignedRoom.description || assignedRoom.name}
                  data-testid={`session-room-pill-${session.id}`}
                >
                  <MapPin className="w-3 h-3" /> {assignedRoom.name}
                </span>
              )}
              {session.location && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> {session.location}
                </span>
              )}
              {assignedSpeakers.length > 0 && (
                <span className="flex items-center gap-1">
                  <Users className="w-3 h-3" /> {assignedSpeakers.length} speaker{assignedSpeakers.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => onEdit(session)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Edit"
            >
              <Edit2 className="w-4 h-4 text-gray-500" />
            </button>
            <button
              onClick={() => onDuplicate(session)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Duplicate"
            >
              <Copy className="w-4 h-4 text-gray-500" />
            </button>
            <button
              onClick={() => onDelete(session.id)}
              className="p-2 hover:bg-red-50 rounded-lg transition-colors"
              title="Delete"
            >
              <Trash2 className="w-4 h-4 text-red-500" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== SESSION EDITOR MODAL ====================

const SessionEditorModal = ({ session, speakers, rooms = [], onSave, onClose, onCreateRoom, allSessions }) => {
  const [editedSession, setEditedSession] = useState({ ...session });
  const [speakerSearch, setSpeakerSearch] = useState('');
  const [liveValidation, setLiveValidation] = useState({ errors: [], warnings: [] });
  const [addingRoom, setAddingRoom] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');
  const [creatingRoom, setCreatingRoom] = useState(false);

  // Live validation on changes
  useEffect(() => {
    const validation = validateSession(editedSession, allSessions);
    setLiveValidation(validation);
  }, [editedSession, allSessions]);

  const filteredSpeakers = speakers.filter(s => 
    s.name.toLowerCase().includes(speakerSearch.toLowerCase()) ||
    s.organization?.toLowerCase().includes(speakerSearch.toLowerCase())
  );

  const assignedSpeakers = speakers.filter(s => editedSession.speaker_ids?.includes(s.id));

  const addSpeaker = (id) => {
    setEditedSession(prev => ({
      ...prev,
      speaker_ids: [...(prev.speaker_ids || []), id]
    }));
    setSpeakerSearch('');
  };

  const removeSpeaker = (id) => {
    setEditedSession(prev => ({
      ...prev,
      speaker_ids: (prev.speaker_ids || []).filter(sid => sid !== id)
    }));
  };

  const getFieldError = (field) => {
    return liveValidation.errors.find(e => e.field === field)?.message;
  };

  const getFieldWarning = (field) => {
    return liveValidation.warnings.find(w => w.field === field)?.message;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between bg-[#00457f] text-white">
          <h3 className="font-semibold text-lg">Edit Session</h3>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Validation Summary */}
        {(liveValidation.errors.length > 0 || liveValidation.warnings.length > 0) && (
          <div className="p-3 bg-gray-50 border-b space-y-1">
            {liveValidation.errors.map((err, i) => (
              <div key={`err-${i}`} className="flex items-center gap-2 text-sm text-red-600">
                <XCircle className="w-4 h-4" />
                {err.message}
              </div>
            ))}
            {liveValidation.warnings.map((warn, i) => (
              <div key={`warn-${i}`} className="flex items-center gap-2 text-sm text-amber-600">
                <AlertTriangle className="w-4 h-4" />
                {warn.message}
              </div>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Session Title *</label>
            <input
              type="text"
              value={editedSession.title || ''}
              onChange={(e) => setEditedSession({ ...editedSession, title: e.target.value })}
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#00457f] ${
                getFieldError('title') ? 'border-red-500 bg-red-50' : ''
              }`}
              placeholder="Enter session title..."
            />
            {getFieldError('title') && (
              <p className="text-xs text-red-500 mt-1">{getFieldError('title')}</p>
            )}
          </div>

          {/* Time & Type Row */}
          <div className="grid grid-cols-3 gap-3">
            <TimeInput
              label="Start Time"
              value={editedSession.start_time}
              onChange={(val) => setEditedSession({ ...editedSession, start_time: val })}
              error={getFieldError('start_time') || getFieldError('time')}
            />
            <TimeInput
              label="End Time"
              value={editedSession.end_time}
              onChange={(val) => setEditedSession({ ...editedSession, end_time: val })}
              error={getFieldError('end_time') || getFieldError('time')}
            />
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
              <select
                value={editedSession.session_type || 'panel'}
                onChange={(e) => setEditedSession({ ...editedSession, session_type: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#00457f]"
              >
                {Object.entries(SESSION_TYPES).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Room & Address/Venue */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Room <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              {!addingRoom ? (
                <div className="flex gap-2">
                  <select
                    value={editedSession.room_id || ''}
                    onChange={(e) => {
                      if (e.target.value === '__add_new__') {
                        setAddingRoom(true);
                      } else {
                        setEditedSession({ ...editedSession, room_id: e.target.value || null });
                      }
                    }}
                    className="flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#00457f]"
                    data-testid="session-room-select"
                  >
                    <option value="">— No room —</option>
                    {rooms.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name}{r.capacity ? ` (cap ${r.capacity})` : ''}
                      </option>
                    ))}
                    <option value="__add_new__">+ Add new room…</option>
                  </select>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="text"
                    autoFocus
                    value={newRoomName}
                    onChange={(e) => setNewRoomName(e.target.value)}
                    placeholder="New room name"
                    className="flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#00457f]"
                    data-testid="session-room-inline-input"
                  />
                  <button
                    type="button"
                    disabled={creatingRoom || !newRoomName.trim()}
                    onClick={async () => {
                      if (!newRoomName.trim() || !onCreateRoom) return;
                      setCreatingRoom(true);
                      const created = await onCreateRoom({ name: newRoomName.trim() });
                      setCreatingRoom(false);
                      if (created?.id) {
                        setEditedSession({ ...editedSession, room_id: created.id });
                        setNewRoomName('');
                        setAddingRoom(false);
                      }
                    }}
                    className="px-3 py-2 bg-[#00457f] text-white rounded-lg text-sm disabled:opacity-50 hover:bg-[#003461]"
                    data-testid="session-room-inline-save"
                  >
                    {creatingRoom ? '…' : 'Add'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setAddingRoom(false); setNewRoomName(''); }}
                    className="px-3 py-2 border rounded-lg text-sm hover:bg-gray-50"
                    data-testid="session-room-inline-cancel"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Address / Venue <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={editedSession.location || ''}
                onChange={(e) => setEditedSession({ ...editedSession, location: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#00457f]"
                placeholder="e.g. University of Tennessee Knoxville"
                data-testid="session-location-input"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
            <RichTextEditor
              value={editedSession.description}
              onChange={(val) => setEditedSession({ ...editedSession, description: val })}
              placeholder="Enter session description..."
            />
          </div>

          {/* Speakers */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-2">Speakers</label>
            
            {assignedSpeakers.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {assignedSpeakers.map(speaker => (
                  <div 
                    key={speaker.id}
                    className="flex items-center gap-2 px-3 py-1.5 bg-[#00457f] text-white rounded-full text-sm"
                  >
                    {speaker.image_url ? (
                      <img src={speaker.image_url} alt="" className="w-5 h-5 rounded-full object-cover" />
                    ) : (
                      <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">
                        {speaker.name.charAt(0)}
                      </div>
                    )}
                    <span>{speaker.name}</span>
                    <button onClick={() => removeSpeaker(speaker.id)} className="hover:bg-white/20 rounded-full p-0.5">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={speakerSearch}
                onChange={(e) => setSpeakerSearch(e.target.value)}
                placeholder="Search speakers to add..."
                className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#00457f]"
              />
              
              {speakerSearch && filteredSpeakers.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {filteredSpeakers.slice(0, 10).map(speaker => (
                    <button
                      key={speaker.id}
                      onClick={() => addSpeaker(speaker.id)}
                      className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2"
                      disabled={editedSession.speaker_ids?.includes(speaker.id)}
                    >
                      {speaker.image_url ? (
                        <img src={speaker.image_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm">
                          {speaker.name.charAt(0)}
                        </div>
                      )}
                      <div>
                        <div className="font-medium text-sm">{speaker.name}</div>
                        <div className="text-xs text-gray-500">{speaker.organization}</div>
                      </div>
                      {editedSession.speaker_ids?.includes(speaker.id) && (
                        <Check className="w-4 h-4 text-green-500 ml-auto" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Display Options */}
          <div className="flex items-center gap-6 pt-2 border-t">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={editedSession.highlighted || false}
                onChange={(e) => setEditedSession({ ...editedSession, highlighted: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-amber-500 focus:ring-amber-500"
              />
              <span className="text-sm text-gray-700">Highlight session</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={editedSession.visible !== false}
                onChange={(e) => setEditedSession({ ...editedSession, visible: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-[#00457f] focus:ring-[#00457f]"
              />
              <span className="text-sm text-gray-700">Show publicly</span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={() => onSave(editedSession)} 
            className="bg-[#00457f] hover:bg-[#003461]"
            disabled={liveValidation.errors.length > 0}
          >
            <Check className="w-4 h-4 mr-2" />
            Apply Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

// ==================== PUBLISH MODAL ====================

const PublishModal = ({ content, draftContent, validation, onPublish, onClose, publishing }) => {
  const [changeNotes, setChangeNotes] = useState('');

  const getChangeSummary = () => {
    // Compare draft vs published to find changes
    const changes = [];
    
    draftContent?.days?.forEach(day => {
      const publishedDay = content?.days?.find(d => d.day_number === day.day_number);
      const publishedSessions = publishedDay?.sessions || [];
      
      day.sessions?.forEach(session => {
        const publishedSession = publishedSessions.find(s => s.id === session.id);
        if (!publishedSession) {
          changes.push({ type: 'added', day: day.day_number, session: session.title });
        } else if (JSON.stringify(session) !== JSON.stringify(publishedSession)) {
          changes.push({ type: 'modified', day: day.day_number, session: session.title });
        }
      });

      // Check for deleted sessions
      publishedSessions.forEach(session => {
        if (!day.sessions?.find(s => s.id === session.id)) {
          changes.push({ type: 'deleted', day: day.day_number, session: session.title });
        }
      });
    });

    return changes;
  };

  const changes = getChangeSummary();
  const hasErrors = validation.totalErrors > 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#00457f] flex items-center justify-center">
              <Send className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">Publish Program Changes</h3>
              <p className="text-sm text-gray-500">Make your changes live on the website</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Validation Status */}
          {hasErrors ? (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 text-red-700 font-medium">
                <XCircle className="w-5 h-5" />
                Cannot publish - {validation.totalErrors} validation error(s)
              </div>
              <p className="text-sm text-red-600 mt-1">
                Please fix all errors before publishing.
              </p>
            </div>
          ) : validation.totalWarnings > 0 ? (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-2 text-amber-700 font-medium">
                <AlertTriangle className="w-5 h-5" />
                {validation.totalWarnings} warning(s) found
              </div>
              <p className="text-sm text-amber-600 mt-1">
                You can still publish, but consider reviewing the warnings.
              </p>
            </div>
          ) : (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 text-green-700 font-medium">
                <CheckCircle className="w-5 h-5" />
                All validations passed
              </div>
            </div>
          )}

          {/* Changes Summary */}
          {changes.length > 0 && (
            <div>
              <h4 className="font-medium text-sm text-gray-700 mb-2">Changes to publish:</h4>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {changes.map((change, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    {change.type === 'added' && <Plus className="w-4 h-4 text-green-500" />}
                    {change.type === 'modified' && <Edit2 className="w-4 h-4 text-blue-500" />}
                    {change.type === 'deleted' && <Trash2 className="w-4 h-4 text-red-500" />}
                    <span className="text-gray-600">Day {change.day}:</span>
                    <span className="font-medium">{change.session}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Change Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Change Notes (optional)
            </label>
            <textarea
              value={changeNotes}
              onChange={(e) => setChangeNotes(e.target.value)}
              placeholder="Describe what changed..."
              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#00457f] h-20"
            />
          </div>
        </div>

        <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={() => onPublish(changeNotes)}
            className="bg-[#00457f] hover:bg-[#003461]"
            disabled={hasErrors || publishing}
          >
            {publishing ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Publishing...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Publish Changes
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ==================== LOGISTICS EDITOR ====================

const LogisticsEditor = ({ logistics, onChange }) => {
  const [expandedItem, setExpandedItem] = useState(null);

  const defaultLogistics = [
    { id: 'venue', title: 'Location & Venue', visible: true },
    { id: 'registration', title: 'Registration & Check-In', visible: true },
    { id: 'credentials', title: 'Credentials', visible: true },
    { id: 'dress', title: 'Dress Code', visible: true },
    { id: 'travel', title: 'Travel & Transportation', visible: true },
    { id: 'hotels', title: 'Hotels & Accommodations', visible: true },
    { id: 'meals', title: 'Meals & Refreshments', visible: true },
    { id: 'contact', title: 'Contact Information', visible: true }
  ];

  const items = logistics?.length > 0 ? logistics : defaultLogistics;

  const updateItem = (id, field, value) => {
    const updated = items.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    );
    onChange(updated);
  };

  return (
    <div className="space-y-2">
      {items.map(item => (
        <div key={item.id} className="bg-white rounded-lg border overflow-hidden">
          <div 
            className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50"
            onClick={() => setExpandedItem(expandedItem === item.id ? null : item.id)}
          >
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={item.visible !== false}
                onChange={(e) => { e.stopPropagation(); updateItem(item.id, 'visible', e.target.checked); }}
                className="w-4 h-4 rounded border-gray-300 text-[#00457f]"
              />
              <span className={`font-medium ${item.visible === false ? 'text-gray-400' : 'text-gray-900'}`}>
                {item.title}
              </span>
            </div>
            {expandedItem === item.id ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </div>
          
          {expandedItem === item.id && (
            <div className="p-4 border-t bg-gray-50">
              <RichTextEditor
                value={item.content}
                onChange={(val) => updateItem(item.id, 'content', val)}
                placeholder={`Enter ${item.title.toLowerCase()} information...`}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ==================== ROOMS MANAGER MODAL ====================

const RoomsManagerModal = ({ rooms, onCreate, onUpdate, onDelete, onClose }) => {
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ name: '', capacity: '', description: '', color: '#00457f' });
  const [adding, setAdding] = useState(false);
  const [newRoom, setNewRoom] = useState({ name: '', capacity: '', description: '', color: '#00457f' });

  const startEdit = (room) => {
    setEditingId(room.id);
    setDraft({
      name: room.name || '',
      capacity: room.capacity ?? '',
      description: room.description || '',
      color: room.color || '#00457f',
    });
  };

  const commitEdit = async (id) => {
    if (!draft.name.trim()) {
      toast.error('Room name cannot be empty');
      return;
    }
    const ok = await onUpdate(id, {
      name: draft.name.trim(),
      capacity: draft.capacity === '' ? null : Number(draft.capacity) || null,
      description: draft.description.trim() || null,
      color: draft.color,
    });
    if (ok) setEditingId(null);
  };

  const commitCreate = async () => {
    if (!newRoom.name.trim()) {
      toast.error('Room name is required');
      return;
    }
    const created = await onCreate({
      name: newRoom.name.trim(),
      capacity: newRoom.capacity === '' ? null : Number(newRoom.capacity) || null,
      description: newRoom.description.trim() || null,
      color: newRoom.color,
    });
    if (created) {
      setNewRoom({ name: '', capacity: '', description: '', color: '#00457f' });
      setAdding(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="rooms-manager-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b flex items-center justify-between bg-[#00457f] text-white">
          <div>
            <h3 className="font-semibold text-lg">Manage Rooms</h3>
            <p className="text-xs text-white/70 mt-0.5">
              Rooms let you tag each session with a track so attendees can filter the program by location.
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded" data-testid="rooms-manager-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {rooms.length === 0 && !adding && (
            <div className="text-center py-10 bg-gray-50 rounded-lg border-2 border-dashed">
              <MapPin className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">No rooms yet. Add your first room to tag sessions by location.</p>
            </div>
          )}

          {rooms.map((room) => {
            const isEditing = editingId === room.id;
            return (
              <div
                key={room.id}
                className="border rounded-lg p-3"
                data-testid={`room-row-${room.id}`}
              >
                {!isEditing ? (
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <span
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-white text-xs font-semibold shrink-0"
                        style={{ backgroundColor: room.color || '#00457f' }}
                      >
                        <MapPin className="w-3 h-3" /> {room.name}
                      </span>
                      <div className="text-sm text-gray-600 min-w-0 truncate">
                        {room.capacity ? <span>Capacity {room.capacity}</span> : null}
                        {room.capacity && room.description ? <span className="mx-2">·</span> : null}
                        {room.description ? <span className="italic">{room.description}</span> : null}
                        {!room.capacity && !room.description ? <span className="italic text-gray-400">No details</span> : null}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => startEdit(room)}
                        className="p-2 hover:bg-gray-100 rounded-lg"
                        title="Edit"
                        data-testid={`room-edit-${room.id}`}
                      >
                        <Edit2 className="w-4 h-4 text-gray-500" />
                      </button>
                      <button
                        onClick={() => onDelete(room.id)}
                        className="p-2 hover:bg-red-50 rounded-lg"
                        title="Delete"
                        data-testid={`room-delete-${room.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                    <input
                      className="md:col-span-2 px-3 py-2 border rounded-lg text-sm"
                      value={draft.name}
                      onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                      placeholder="Room name"
                      data-testid={`room-name-input-${room.id}`}
                    />
                    <input
                      className="px-3 py-2 border rounded-lg text-sm"
                      type="number"
                      min="0"
                      value={draft.capacity}
                      onChange={(e) => setDraft({ ...draft, capacity: e.target.value })}
                      placeholder="Capacity"
                      data-testid={`room-capacity-input-${room.id}`}
                    />
                    <input
                      className="md:col-span-1 px-3 py-2 border rounded-lg text-sm"
                      value={draft.description}
                      onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                      placeholder="Description"
                      data-testid={`room-desc-input-${room.id}`}
                    />
                    <div className="flex gap-1 items-center">
                      <input
                        type="color"
                        className="w-10 h-10 rounded border p-0"
                        value={draft.color}
                        onChange={(e) => setDraft({ ...draft, color: e.target.value })}
                        title="Pill color"
                        data-testid={`room-color-input-${room.id}`}
                      />
                      <button
                        onClick={() => commitEdit(room.id)}
                        className="px-3 py-2 bg-[#00457f] text-white rounded-lg text-sm hover:bg-[#003461]"
                        data-testid={`room-save-${room.id}`}
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        className="px-3 py-2 border rounded-lg text-sm hover:bg-gray-50"
                        data-testid={`room-cancel-${room.id}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {adding ? (
            <div className="border-2 border-dashed border-[#00457f] rounded-lg p-3 bg-blue-50/40">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                <input
                  autoFocus
                  className="md:col-span-2 px-3 py-2 border rounded-lg text-sm"
                  value={newRoom.name}
                  onChange={(e) => setNewRoom({ ...newRoom, name: e.target.value })}
                  placeholder="Room name (e.g. Ballroom A)"
                  data-testid="new-room-name"
                />
                <input
                  className="px-3 py-2 border rounded-lg text-sm"
                  type="number"
                  min="0"
                  value={newRoom.capacity}
                  onChange={(e) => setNewRoom({ ...newRoom, capacity: e.target.value })}
                  placeholder="Capacity"
                  data-testid="new-room-capacity"
                />
                <input
                  className="px-3 py-2 border rounded-lg text-sm"
                  value={newRoom.description}
                  onChange={(e) => setNewRoom({ ...newRoom, description: e.target.value })}
                  placeholder="Description"
                  data-testid="new-room-desc"
                />
                <div className="flex gap-1 items-center">
                  <input
                    type="color"
                    className="w-10 h-10 rounded border p-0"
                    value={newRoom.color}
                    onChange={(e) => setNewRoom({ ...newRoom, color: e.target.value })}
                    title="Pill color"
                    data-testid="new-room-color"
                  />
                  <button
                    onClick={commitCreate}
                    disabled={!newRoom.name.trim()}
                    className="px-3 py-2 bg-[#00457f] text-white rounded-lg text-sm hover:bg-[#003461] disabled:opacity-50"
                    data-testid="new-room-save"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => { setAdding(false); setNewRoom({ name: '', capacity: '', description: '', color: '#00457f' }); }}
                    className="px-3 py-2 border rounded-lg text-sm hover:bg-gray-50"
                    data-testid="new-room-cancel"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setAdding(true)}
              className="w-full border-2 border-dashed border-gray-300 hover:border-[#00457f] hover:bg-blue-50/40 rounded-lg py-3 text-sm text-gray-500 hover:text-[#00457f] transition-colors flex items-center justify-center gap-2"
              data-testid="new-room-add-btn"
            >
              <Plus className="w-4 h-4" /> Add Room
            </button>
          )}
        </div>

        <div className="p-3 border-t bg-gray-50 flex justify-end">
          <Button variant="outline" onClick={onClose} data-testid="rooms-manager-done">Done</Button>
        </div>
      </div>
    </div>
  );
};

// ==================== ADMIN ROOM GRID (parallel track preview) ====================

const AdminGridCard = ({ session, speakers, rooms, validation, isDraft, onEdit, onDelete, onDuplicate }) => {
  const typeConfig = SESSION_TYPES[session.session_type] || SESSION_TYPES.panel;
  const assignedRoom = session.room_id ? rooms.find(r => r.id === session.room_id) : null;
  const assignedSpeakers = speakers.filter(s => session.speaker_ids?.includes(s.id));
  const hasErrors = validation?.errors?.length > 0;
  const hasWarnings = validation?.warnings?.length > 0;

  return (
    <div
      className={`bg-white border rounded-lg p-3 transition-shadow hover:shadow-md ${
        hasErrors ? 'border-red-300 bg-red-50/40' : hasWarnings ? 'border-amber-300' : 'border-gray-200'
      } ${session.visible === false ? 'opacity-60' : ''}`}
      data-testid={`admin-grid-card-${session.id}`}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-1 text-[11px] text-gray-500 min-w-0">
          <Clock className="w-3 h-3 flex-shrink-0" />
          <span className="truncate">
            {session.start_time || session.end_time
              ? `${session.start_time || '--:--'} – ${session.end_time || '--:--'}`
              : 'TBD'}
          </span>
        </div>
        <div className="flex items-center gap-0.5">
          <button
            onClick={() => onEdit(session)}
            className="p-1 hover:bg-gray-100 rounded"
            title="Edit"
            data-testid={`admin-grid-edit-${session.id}`}
          >
            <Edit2 className="w-3.5 h-3.5 text-gray-500" />
          </button>
          <button
            onClick={() => onDuplicate(session)}
            className="p-1 hover:bg-gray-100 rounded"
            title="Duplicate"
            data-testid={`admin-grid-dup-${session.id}`}
          >
            <Copy className="w-3.5 h-3.5 text-gray-500" />
          </button>
          <button
            onClick={() => onDelete(session.id)}
            className="p-1 hover:bg-red-50 rounded"
            title="Delete"
            data-testid={`admin-grid-del-${session.id}`}
          >
            <Trash2 className="w-3.5 h-3.5 text-red-500" />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-1 flex-wrap mb-1.5">
        <span
          className="text-[10px] px-1.5 py-0.5 rounded-full font-medium text-white"
          style={{ backgroundColor: typeConfig.color }}
        >
          {typeConfig.label}
        </span>
        {session.highlighted && <Star className="w-3 h-3 text-amber-500 fill-amber-500" />}
        {session.visible === false && <EyeOff className="w-3 h-3 text-gray-400" />}
        {hasErrors && <XCircle className="w-3 h-3 text-red-500" title={validation.errors.map(e => e.message).join(', ')} />}
        {!hasErrors && hasWarnings && <AlertTriangle className="w-3 h-3 text-amber-500" title={validation.warnings.map(w => w.message).join(', ')} />}
        {isDraft && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">Draft</span>
        )}
      </div>

      <h4 className="text-sm font-semibold text-gray-900 leading-tight line-clamp-2" title={session.title}>
        {session.title || 'Untitled Session'}
      </h4>

      {(session.location || assignedSpeakers.length > 0) && (
        <div className="mt-2 pt-2 border-t border-gray-100 text-[11px] text-gray-500 space-y-0.5">
          {session.location && (
            <div className="flex items-center gap-1 truncate" title={session.location}>
              <MapPin className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{session.location}</span>
            </div>
          )}
          {assignedSpeakers.length > 0 && (
            <div className="flex items-center gap-1">
              <Users className="w-3 h-3 flex-shrink-0" />
              <span>{assignedSpeakers.length} speaker{assignedSpeakers.length !== 1 ? 's' : ''}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AdminRoomGrid = ({ sessions, rooms, speakers, sessionValidations, hasUnsavedChanges, onEdit, onDelete, onDuplicate, onSwitchToList }) => {
  // Columns = rooms-in-use for the current day's sessions, in rooms[] order
  const usedRoomIds = Array.from(new Set(sessions.map(s => s.room_id).filter(Boolean)));
  const roomsInUse = rooms.filter(r => usedRoomIds.includes(r.id));
  const hasUnroomed = sessions.some(s => !s.room_id);
  const columns = [
    ...roomsInUse.map(r => ({ key: r.id, room: r })),
    ...(hasUnroomed ? [{ key: 'none', room: null }] : []),
  ];

  // Group + sort by start time (sessions without start time sink to the bottom)
  const groups = Object.fromEntries(columns.map(c => [c.key, []]));
  for (const s of sessions) groups[s.room_id || 'none']?.push(s);
  for (const key of Object.keys(groups)) {
    groups[key].sort((a, b) => {
      const ta = parseTimeToMinutes(a.start_time);
      const tb = parseTimeToMinutes(b.start_time);
      if (ta === null && tb === null) return (a.order || 0) - (b.order || 0);
      if (ta === null) return 1;
      if (tb === null) return -1;
      return ta - tb;
    });
  }

  return (
    <div className="space-y-3" data-testid="admin-room-grid">
      <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-900">
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <div>
          Preview-only: parallel tracks grouped by Room, sorted by start time. Drag-to-reorder is disabled here —
          <button
            onClick={onSwitchToList}
            className="underline font-semibold ml-1 hover:text-blue-700"
            data-testid="admin-grid-switch-to-list"
          >
            switch to List
          </button>
          {' '}to reorder sessions.
        </div>
      </div>
      <div
        className="grid gap-3 overflow-x-auto pb-2"
        style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(240px, 1fr))` }}
      >
        {columns.map((col) => (
          <div key={col.key} className="flex flex-col gap-2 min-w-0" data-testid={`admin-room-column-${col.key}`}>
            <div
              className="sticky top-0 z-[1] inline-flex items-center gap-1 self-start px-2.5 py-1 rounded-full text-[11px] font-semibold text-white shadow-sm"
              style={{ backgroundColor: col.room?.color || '#334155' }}
            >
              <MapPin className="w-3 h-3" />
              {col.room ? col.room.name : 'No room'}
              {col.room?.capacity ? <span className="opacity-80 ml-0.5">· cap {col.room.capacity}</span> : null}
              <span className="opacity-80 ml-1">· {groups[col.key].length}</span>
            </div>
            <div className="space-y-2">
              {groups[col.key].length === 0 ? (
                <div className="text-center text-[11px] text-gray-400 italic py-3 border border-dashed border-gray-200 rounded-lg">
                  No sessions
                </div>
              ) : (
                groups[col.key].map((session) => (
                  <AdminGridCard
                    key={session.id}
                    session={session}
                    speakers={speakers}
                    rooms={rooms}
                    validation={sessionValidations[session.id]}
                    isDraft={hasUnsavedChanges}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onDuplicate={onDuplicate}
                  />
                ))
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ==================== MAIN COMPONENT ====================

const AdminProgram = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('schedule');
  const [activeDay, setActiveDay] = useState(1);
  const [publishedContent, setPublishedContent] = useState(null);
  const [draftContent, setDraftContent] = useState(null);
  const [speakers, setSpeakers] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [showRoomsModal, setShowRoomsModal] = useState(false);
  const [scheduleViewByDay, setScheduleViewByDay] = useState({}); // {dayNumber: 'list' | 'grid'}
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [activeSessionId, setActiveSessionId] = useState(null);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Validation
  const validation = useMemo(() => {
    return validateAllSessions(draftContent?.days);
  }, [draftContent]);

  // Check for unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    return JSON.stringify(publishedContent) !== JSON.stringify(draftContent);
  }, [publishedContent, draftContent]);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [contentRes, speakersRes, roomsRes] = await Promise.all([
        fetch(`${API_URL}/api/content/program`),
        fetch(`${API_URL}/api/speakers`),
        fetch(`${API_URL}/api/content/program/rooms`),
      ]);
      
      if (contentRes.ok) {
        const data = await contentRes.json();
        setPublishedContent(data);
        setDraftContent(JSON.parse(JSON.stringify(data))); // Deep clone for draft
      }
      
      if (speakersRes.ok) {
        const data = await speakersRes.json();
        setSpeakers(data);
      }

      if (roomsRes.ok) {
        setRooms(await roomsRes.json());
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Failed to load program data');
    } finally {
      setLoading(false);
    }
  };

  // ===== Rooms CRUD =====
  const refreshRooms = async () => {
    try {
      const res = await fetch(`${API_URL}/api/content/program/rooms`);
      if (res.ok) setRooms(await res.json());
    } catch { /* non-fatal */ }
  };

  const createRoom = async (payload) => {
    try {
      const res = await fetch(`${API_URL}/api/content/program/rooms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || `Failed to create room (${res.status})`);
      }
      const room = await res.json();
      await refreshRooms();
      toast.success(`Room "${room.name}" added`);
      return room;
    } catch (e) {
      toast.error(e.message || 'Failed to create room');
      return null;
    }
  };

  const updateRoom = async (id, patch) => {
    try {
      const res = await fetch(`${API_URL}/api/content/program/rooms/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error('Failed to update room');
      await refreshRooms();
      return true;
    } catch (e) {
      toast.error(e.message || 'Failed to update room');
      return false;
    }
  };

  const deleteRoom = async (id) => {
    const room = rooms.find(r => r.id === id);
    if (!window.confirm(`Delete room "${room?.name || id}"? Any sessions using it will keep their title but lose the room assignment.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/content/program/rooms/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete room');
      await refreshRooms();
      // Clean up any room_id references in current draft content so UI reflects reality
      setDraftContent(prev => prev ? {
        ...prev,
        days: (prev.days || []).map(d => ({
          ...d,
          sessions: (d.sessions || []).map(s => s.room_id === id ? { ...s, room_id: null } : s),
        })),
      } : prev);
      toast.success('Room deleted');
    } catch (e) {
      toast.error(e.message || 'Failed to delete room');
    }
  };

  const saveDraft = async () => {
    try {
      setSaving(true);
      // In a real implementation, you'd save to a separate draft endpoint
      // For now, we just update the state
      toast.success('Draft saved');
    } catch (err) {
      toast.error('Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  const publishContent = async (changeNotes) => {
    try {
      setPublishing(true);
      const response = await fetch(`${API_URL}/api/content/program`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(draftContent)
      });
      
      if (response.ok) {
        setPublishedContent(JSON.parse(JSON.stringify(draftContent)));
        toast.success('Program published successfully!');
        setShowPublishModal(false);
      } else {
        throw new Error('Failed to publish');
      }
    } catch (err) {
      toast.error('Failed to publish program');
    } finally {
      setPublishing(false);
    }
  };

  const discardChanges = () => {
    if (window.confirm('Discard all unpublished changes?')) {
      setDraftContent(JSON.parse(JSON.stringify(publishedContent)));
      toast.info('Changes discarded');
    }
  };

  const seedContent = async () => {
    try {
      const response = await fetch(`${API_URL}/api/content/program/seed`, { method: 'POST' });
      const data = await response.json();
      toast.success(data.message);
      fetchData();
    } catch (err) {
      toast.error('Failed to seed content');
    }
  };

  // Session Management
  const getCurrentDaySessions = () => {
    const day = draftContent?.days?.find(d => d.day_number === activeDay);
    return day?.sessions || [];
  };

  const getAllSessions = () => {
    return draftContent?.days?.flatMap(day => day.sessions || []) || [];
  };

  const updateSession = (updatedSession) => {
    setDraftContent(prev => ({
      ...prev,
      days: prev.days.map(day => ({
        ...day,
        sessions: day.sessions.map(session =>
          session.id === updatedSession.id ? updatedSession : session
        )
      }))
    }));
    setEditingSession(null);
  };

  const addSession = () => {
    const newSession = {
      id: `session-${Date.now()}`,
      title: 'New Session',
      description: '',
      start_time: '',
      end_time: '',
      session_type: 'panel',
      speaker_ids: [],
      location: '',
      day: activeDay,
      order: getCurrentDaySessions().length,
      visible: true,
      highlighted: false
    };

    setDraftContent(prev => ({
      ...prev,
      days: prev.days.map(day =>
        day.day_number === activeDay
          ? { ...day, sessions: [...(day.sessions || []), newSession] }
          : day
      )
    }));

    setEditingSession(newSession);
  };

  const deleteSession = (sessionId) => {
    if (!window.confirm('Delete this session?')) return;
    
    setDraftContent(prev => ({
      ...prev,
      days: prev.days.map(day => ({
        ...day,
        sessions: day.sessions.filter(s => s.id !== sessionId)
      }))
    }));
  };

  const duplicateSession = (session) => {
    const newSession = {
      ...session,
      id: `session-${Date.now()}`,
      title: `${session.title} (Copy)`,
      order: getCurrentDaySessions().length
    };

    setDraftContent(prev => ({
      ...prev,
      days: prev.days.map(day =>
        day.day_number === activeDay
          ? { ...day, sessions: [...(day.sessions || []), newSession] }
          : day
      )
    }));
  };

  // Drag and Drop Handler
  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      setDraftContent(prev => ({
        ...prev,
        days: prev.days.map(day => {
          if (day.day_number !== activeDay) return day;
          
          const sessions = [...(day.sessions || [])];
          const oldIndex = sessions.findIndex(s => s.id === active.id);
          const newIndex = sessions.findIndex(s => s.id === over.id);
          
          const reordered = arrayMove(sessions, oldIndex, newIndex).map((s, i) => ({
            ...s,
            order: i
          }));
          
          return { ...day, sessions: reordered };
        })
      }));
    }
  };

  const sessions = getCurrentDaySessions().sort((a, b) => (a.order || 0) - (b.order || 0));

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-[#00457f]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Draft & Publish Controls */}
        <DraftControls draftKey="program" pageLabel="Program" previewPath="/program" />

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Program Manager</h1>
            <p className="text-gray-600">Build and manage the event schedule</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={seedContent}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Seed Data
            </Button>
            {hasUnsavedChanges && (
              <Button variant="outline" onClick={discardChanges}>
                <RotateCcw className="w-4 h-4 mr-2" />
                Discard
              </Button>
            )}
            <Button 
              onClick={() => setShowPublishModal(true)}
              className="bg-[#00457f] hover:bg-[#003461]"
              disabled={!hasUnsavedChanges}
            >
              <Send className="w-4 h-4 mr-2" />
              Publish
              {hasUnsavedChanges && (
                <span className="ml-2 w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
              )}
            </Button>
          </div>
        </div>

        {/* Validation Summary Bar */}
        {(validation.totalErrors > 0 || validation.totalWarnings > 0) && (
          <div className={`p-3 rounded-lg flex items-center justify-between ${
            validation.totalErrors > 0 ? 'bg-red-50 border border-red-200' : 'bg-amber-50 border border-amber-200'
          }`}>
            <div className="flex items-center gap-4">
              {validation.totalErrors > 0 && (
                <span className="flex items-center gap-2 text-red-700">
                  <XCircle className="w-5 h-5" />
                  {validation.totalErrors} error{validation.totalErrors !== 1 ? 's' : ''}
                </span>
              )}
              {validation.totalWarnings > 0 && (
                <span className="flex items-center gap-2 text-amber-700">
                  <AlertTriangle className="w-5 h-5" />
                  {validation.totalWarnings} warning{validation.totalWarnings !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <span className="text-sm text-gray-600">
              {validation.totalErrors > 0 
                ? 'Fix errors before publishing' 
                : 'Review warnings (optional)'}
            </span>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 border-b">
          {[
            { id: 'schedule', label: 'Schedule Builder', icon: Calendar },
            { id: 'logistics', label: 'Logistics Content', icon: FileText },
            { id: 'settings', label: 'Page Settings', icon: Settings }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors border-b-2 -mb-px ${
                activeTab === tab.id
                  ? 'text-[#00457f] border-[#00457f]'
                  : 'text-gray-500 border-transparent hover:text-gray-700'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Schedule Builder Tab */}
        {activeTab === 'schedule' && (
          <div className="space-y-4">
            {/* Day Management Section */}
            <Card className="bg-gradient-to-r from-[#00457f]/5 to-transparent border-[#00457f]/20">
              <CardContent className="py-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-[#00457f]" />
                    Event Days
                  </h3>
                  <Button 
                    onClick={() => {
                      const newDayNumber = (draftContent?.days?.length || 0) + 1;
                      const newDay = {
                        day_number: newDayNumber,
                        date: `Day ${newDayNumber}`,
                        label: `Day ${newDayNumber}`,
                        sessions: []
                      };
                      setDraftContent(prev => ({
                        ...prev,
                        days: [...(prev.days || []), newDay]
                      }));
                      setActiveDay(newDayNumber);
                      toast.success(`Day ${newDayNumber} added`);
                    }}
                    variant="outline" 
                    size="sm"
                    className="text-[#00457f] border-[#00457f] hover:bg-[#00457f] hover:text-white"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Day
                  </Button>
                </div>
                
                {/* Day Tabs with Edit Capability */}
                <div className="flex flex-wrap items-center gap-2">
                  {draftContent?.days?.map((day, index) => (
                    <div 
                      key={day.day_number}
                      className={`group relative flex items-center rounded-lg transition-all ${
                        activeDay === day.day_number
                          ? 'bg-[#00457f] text-white shadow-lg'
                          : 'bg-white text-gray-700 border hover:border-[#00457f]/50'
                      }`}
                    >
                      <button
                        onClick={() => setActiveDay(day.day_number)}
                        className="flex items-center gap-2 px-4 py-2 font-medium"
                      >
                        <Calendar className="w-4 h-4" />
                        <span>{day.date || `Day ${day.day_number}`}</span>
                        <span className="text-xs opacity-70">
                          ({day.sessions?.length || 0} sessions)
                        </span>
                        {day.visible === false && (
                          <span
                            data-testid={`day-hidden-badge-${day.day_number}`}
                            className={`ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded ${
                              activeDay === day.day_number ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-700'
                            }`}
                            title="This day is hidden on the public site"
                          >
                            Hidden
                          </span>
                        )}
                      </button>
                      
                      {/* Edit & Delete buttons */}
                      <div className={`flex items-center border-l ${
                        activeDay === day.day_number ? 'border-white/30' : 'border-gray-200'
                      }`}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const newDate = prompt('Enter date (e.g., "February 12, 2027" or "Day 1 - Wednesday"):', day.date);
                            if (newDate !== null && newDate.trim()) {
                              setDraftContent(prev => ({
                                ...prev,
                                days: prev.days.map(d => 
                                  d.day_number === day.day_number 
                                    ? { ...d, date: newDate.trim() }
                                    : d
                                )
                              }));
                            }
                          }}
                          className={`p-2 transition-colors ${
                            activeDay === day.day_number 
                              ? 'hover:bg-white/20' 
                              : 'hover:bg-gray-100'
                          }`}
                          title="Edit date"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        {draftContent?.days?.length > 1 && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm(`Delete "${day.date}"? This will remove all sessions for this day.`)) {
                                setDraftContent(prev => {
                                  const updatedDays = prev.days
                                    .filter(d => d.day_number !== day.day_number)
                                    .map((d, i) => ({ ...d, day_number: i + 1 }));
                                  return { ...prev, days: updatedDays };
                                });
                                if (activeDay === day.day_number) {
                                  setActiveDay(1);
                                }
                                toast.success('Day removed');
                              }
                            }}
                            className={`p-2 transition-colors ${
                              activeDay === day.day_number 
                                ? 'hover:bg-red-500/50 text-white' 
                                : 'hover:bg-red-50 text-red-500'
                            }`}
                            title="Delete day"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Current Day Details */}
                {draftContent?.days?.find(d => d.day_number === activeDay) && (
                  <div className="mt-4 p-3 bg-white rounded-lg border">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Date Display</label>
                        <input
                          type="text"
                          value={draftContent.days.find(d => d.day_number === activeDay)?.date || ''}
                          onChange={(e) => {
                            setDraftContent(prev => ({
                              ...prev,
                              days: prev.days.map(d => 
                                d.day_number === activeDay 
                                  ? { ...d, date: e.target.value }
                                  : d
                              )
                            }));
                          }}
                          placeholder="e.g., February 12, 2027"
                          className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#00457f] focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Day Label (optional)</label>
                        <input
                          type="text"
                          value={draftContent.days.find(d => d.day_number === activeDay)?.label || ''}
                          onChange={(e) => {
                            setDraftContent(prev => ({
                              ...prev,
                              days: prev.days.map(d => 
                                d.day_number === activeDay 
                                  ? { ...d, label: e.target.value }
                                  : d
                              )
                            }));
                          }}
                          placeholder="e.g., Opening Day, Main Conference"
                          className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#00457f] focus:border-transparent"
                        />
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t flex items-center justify-between">
                      <div>
                        <div className="text-xs font-medium text-gray-700">Day visibility</div>
                        <div className="text-[11px] text-gray-500">Hidden days render as a &quot;Program details coming soon&quot; placeholder on the public site.</div>
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          data-testid={`day-visible-toggle-${activeDay}`}
                          checked={draftContent.days.find(d => d.day_number === activeDay)?.visible !== false}
                          onChange={(e) => {
                            const nextVisible = e.target.checked;
                            setDraftContent(prev => ({
                              ...prev,
                              days: prev.days.map(d =>
                                d.day_number === activeDay
                                  ? { ...d, visible: nextVisible }
                                  : d
                              )
                            }));
                          }}
                          className="w-4 h-4 text-[#00457f] focus:ring-[#00457f] border-gray-300 rounded"
                        />
                        <span className="text-sm text-gray-700">
                          {draftContent.days.find(d => d.day_number === activeDay)?.visible !== false ? 'Visible' : 'Hidden'}
                        </span>
                      </label>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Sessions for Active Day */}
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="font-semibold text-gray-700">
                Sessions for {draftContent?.days?.find(d => d.day_number === activeDay)?.date || `Day ${activeDay}`}
              </h3>
              <div className="flex items-center gap-2">
                {/* View toggle — only meaningful when at least one session is tagged with a room */}
                {sessions.some(s => s.room_id) && (
                  <div className="inline-flex rounded-lg border bg-white overflow-hidden" data-testid="admin-view-toggle">
                    <button
                      onClick={() => setScheduleViewByDay(prev => ({ ...prev, [activeDay]: 'list' }))}
                      className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                        (scheduleViewByDay[activeDay] || 'list') === 'list'
                          ? 'bg-[#00457f] text-white'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                      data-testid="admin-view-list"
                      title="Vertical list (drag to reorder)"
                    >
                      List
                    </button>
                    <button
                      onClick={() => setScheduleViewByDay(prev => ({ ...prev, [activeDay]: 'grid' }))}
                      className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                        scheduleViewByDay[activeDay] === 'grid'
                          ? 'bg-[#00457f] text-white'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                      data-testid="admin-view-grid"
                      title="Preview parallel tracks per room"
                    >
                      Grid
                    </button>
                  </div>
                )}
                <Button
                  onClick={() => setShowRoomsModal(true)}
                  variant="outline"
                  data-testid="manage-rooms-btn"
                >
                  <MapPin className="w-4 h-4 mr-2" />
                  Manage Rooms{rooms.length ? ` (${rooms.length})` : ''}
                </Button>
                <Button onClick={addSession} variant="outline" data-testid="add-session-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Session
                </Button>
              </div>
            </div>

            {/* Sessions — List (drag-sortable) or Grid (room columns, preview-only) */}
            {(scheduleViewByDay[activeDay] === 'grid' && sessions.some(s => s.room_id)) ? (
              <AdminRoomGrid
                sessions={sessions}
                rooms={rooms}
                speakers={speakers}
                sessionValidations={validation.sessionValidations}
                hasUnsavedChanges={hasUnsavedChanges}
                onEdit={setEditingSession}
                onDelete={deleteSession}
                onDuplicate={duplicateSession}
                onSwitchToList={() => setScheduleViewByDay(prev => ({ ...prev, [activeDay]: 'list' }))}
              />
            ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={sessions.map(s => s.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-3">
                  {sessions.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed">
                      <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">No sessions for this day</p>
                      <Button onClick={addSession} variant="outline" className="mt-3">
                        <Plus className="w-4 h-4 mr-2" />
                        Add First Session
                      </Button>
                    </div>
                  ) : (
                    sessions.map(session => (
                      <SortableSessionCard
                        key={session.id}
                        session={session}
                        onEdit={setEditingSession}
                        onDelete={deleteSession}
                        onDuplicate={duplicateSession}
                        speakers={speakers}
                        rooms={rooms}
                        validation={validation.sessionValidations[session.id]}
                        isDraft={hasUnsavedChanges}
                      />
                    ))
                  )}
                </div>
              </SortableContext>
            </DndContext>
            )}
          </div>
        )}

        {/* Logistics Tab */}
        {activeTab === 'logistics' && (
          <LogisticsEditor
            logistics={draftContent?.logistics}
            onChange={(updated) => setDraftContent(prev => ({ ...prev, logistics: updated }))}
          />
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <Card>
            <CardHeader>
              <CardTitle>Page Settings</CardTitle>
              <CardDescription>Configure program page display options</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Page Title</label>
                <input
                  type="text"
                  value={draftContent?.page_title || 'Program'}
                  onChange={(e) => setDraftContent(prev => ({ ...prev, page_title: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Page Subtitle</label>
                <input
                  type="text"
                  value={draftContent?.page_subtitle || ''}
                  onChange={(e) => setDraftContent(prev => ({ ...prev, page_subtitle: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Optional subtitle..."
                />
              </div>

              <div className="border-t pt-4 space-y-4">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">Placeholder Copy</h4>
                  <p className="text-xs text-gray-500">Shown publicly in place of hidden days or sessions. Tweak per event (e.g., &quot;TBA&quot;, &quot;Under embargo&quot;).</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hidden day placeholder</label>
                  <input
                    type="text"
                    data-testid="program-day-placeholder-text"
                    value={draftContent?.day_placeholder_text ?? 'Program details coming soon'}
                    onChange={(e) => setDraftContent(prev => ({ ...prev, day_placeholder_text: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="e.g., Program details coming soon"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hidden session placeholder</label>
                  <input
                    type="text"
                    data-testid="program-session-placeholder-text"
                    value={draftContent?.session_placeholder_text ?? 'Details coming soon'}
                    onChange={(e) => setDraftContent(prev => ({ ...prev, session_placeholder_text: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="e.g., Details coming soon"
                  />
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="text-sm font-semibold text-gray-900">Sanity-check hidden items</h4>
                <p className="text-xs text-gray-500 mb-2">
                  Opens the public program in preview mode with every hidden day and session revealed — useful before publishing.
                </p>
                <a
                  data-testid="program-preview-hidden-link"
                  href="/program?preview=1&show_hidden=1"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#00457f] text-[#00457f] hover:bg-[#00457f] hover:text-white transition-colors text-sm font-medium"
                >
                  <Eye className="w-4 h-4" />
                  Preview with hidden days
                </a>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Session Editor Modal */}
      {editingSession && (
        <SessionEditorModal
          session={editingSession}
          speakers={speakers}
          rooms={rooms}
          onCreateRoom={createRoom}
          allSessions={getAllSessions()}
          onSave={updateSession}
          onClose={() => setEditingSession(null)}
        />
      )}

      {/* Rooms Manager Modal */}
      {showRoomsModal && (
        <RoomsManagerModal
          rooms={rooms}
          onCreate={createRoom}
          onUpdate={updateRoom}
          onDelete={deleteRoom}
          onClose={() => setShowRoomsModal(false)}
        />
      )}

      {/* Publish Modal */}
      {showPublishModal && (
        <PublishModal
          content={publishedContent}
          draftContent={draftContent}
          validation={validation}
          onPublish={publishContent}
          onClose={() => setShowPublishModal(false)}
          publishing={publishing}
        />
      )}

      {/* Bulk Convert Speakers Tool */}
      <div className="max-w-5xl mx-auto px-4 pb-4 pt-6">
        <BulkConvertSpeakers />
      </div>

      {/* Additional Template Sections */}
      <div className="max-w-5xl mx-auto px-4 pb-8">
        <PageSectionsManager page="program" title="Additional Sections" />
      </div>
    </AdminLayout>
  );
};

export default AdminProgram;
